import api from '../../interceptors/axios';

const DB_NAME = "EnsarasOfflineDB";
const STORE_NAME = "entries";
const DB_VERSION = 1;

export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "localId" });
      }
    };
    request.onsuccess = (e) => resolve(e.target.result);
    request.onerror = (e) => reject(e.target.error);
  });
}

export async function saveEntry(entry) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(entry);
    request.onsuccess = () => {
      window.dispatchEvent(new CustomEvent("offline-db-updated"));
      resolve(entry);
    };
    request.onerror = (e) => reject(e.target.error);
  });
}

export async function getEntries() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
}

export async function deleteEntry(localId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(localId);
    request.onsuccess = () => {
      window.dispatchEvent(new CustomEvent("offline-db-updated"));
      resolve();
    };
    request.onerror = (e) => reject(e.target.error);
  });
}

export async function clearSavedEntries() {
  try {
    const entries = await getEntries();
    const savedEntries = entries.filter((e) => e.status === "saved");
    for (const entry of savedEntries) {
      await deleteEntry(entry.localId);
    }
  } catch (err) {
    console.error("Error clearing saved entries:", err);
  }
}

export async function syncOfflineEntries(onProgress) {
  try {
    const entries = await getEntries();
    const toSync = entries.filter(e => e.status === "queued" || e.status === "failed");
    
    for (const entry of toSync) {
      try {
        entry.status = "syncing";
        await saveEntry(entry);
        if (onProgress) onProgress();

        const isEdit = typeof entry.localId === 'string' && entry.localId.startsWith('local-edit-');
        let response;
        const basePath = entry.basePath || '/v1/form-data/';
        if (isEdit) {
          const backendId = entry.localId.replace('local-edit-', '');
          const url = basePath.endsWith('/') ? `${basePath}${backendId}/` : `${basePath}/${backendId}/`;
          response = await api.put(url, entry.data, {
            params: entry.metadata,
            headers: { 'Content-Type': 'application/json' },
          });
        } else {
          response = await api.post(basePath, entry.data, {
            params: entry.metadata,
            headers: { 'Content-Type': 'application/json' },
          });
        }

        entry.status = "saved";
        entry.data = { ...entry.data, id: response.data?.data?.id || response.data?.id };
        entry.error = null;
        await saveEntry(entry);
        if (onProgress) onProgress();
      } catch (err) {
        console.error("Failed to sync entry:", err);
        entry.status = "failed";
        entry.error = err?.response?.data?.message || err?.message || "Sync failed";
        await saveEntry(entry);
        if (onProgress) onProgress();
      }
    }
    // Clean up successfully saved entries after sync has completed
    await clearSavedEntries();
  } catch (err) {
    console.error("Error in syncOfflineEntries:", err);
  }
}
